import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  FolderGit2,
  GitBranch,
  Plus,
  Play,
  Moon,
  Copy,
  Archive,
  ShieldCheck,
  RefreshCw,
  Cpu,
  ListTodo,
  HardDrive,
  Clock,
  Sparkles,
  Network,
  GitCommitHorizontal,
  Activity,
  KeyRound,
  ArrowRight,
  Camera,
  GitFork,
  AlertTriangle,
  Lightbulb,
  CheckCircle2,
  XCircle,
  Github,
  Gitlab,
  Box,
  Plug,
  Server,
  ExternalLink,
  ArchiveRestore,
  FolderOpen,
  Cloud,
} from "lucide-react";
import { useHive } from "@/store/useHive";
import { useSampleDataCta } from "@/hooks/useSampleDataCta";
import { aresWorkspaceUrl, openExternalApp } from "@/lib/appLinks";
import { APPS } from "@/data/apps";
import { isEnabled, isVisible } from "@/lib/entitlements";
import {
  WS_ENV_LABEL,
  WS_STATUS_LABEL,
  WS_STATUS_TONE,
  WS_PROVIDER_LABEL,
  byoncPolicyLabel,
  isGitSource,
} from "@/data/workspaces";
import { AppHeader, Badge, Card, Dot, Progress, SectionTitle } from "@/components/ui/primitives";
import { SampleBadge } from "@/components/ui/SampleBadge";
import { WorkspaceWizard } from "./workspace/WorkspaceWizard";
import type { CodeWorkspace } from "@/types";

const accent = APPS.workspaces.accent;

const PROVIDER_ICON = { github: Github, gitlab: Gitlab, bitbucket: Box, local: FolderOpen, s3: Cloud } as const;

/** Launch the real ARES IDE on this workspace (hidden when VITE_ARES_URL unset). */
function launchInAres(workspaceId: string) {
  const url = aresWorkspaceUrl(workspaceId);
  if (!url) return;
  openExternalApp(url);
  useHive.getState().pushToast({
    title: "Opening in ARES",
    body: "Launching the IDE on this workspace — signed in via Hive",
    tone: "info",
  });
}

function IconButton({
  icon: Icon,
  label,
  onClick,
  primary,
  danger,
}: {
  icon: any;
  label: string;
  onClick: (e: React.MouseEvent) => void;
  primary?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      title={label}
      className="group/btn relative grid h-7 w-7 place-items-center rounded-lg border transition"
      style={{
        borderColor: primary ? accent : "hsl(var(--border))",
        background: primary ? `${accent}1f` : "hsl(var(--surface-2) / 0.5)",
        color: primary ? accent : danger ? "#f43f5e" : "var(--text-1)",
      }}
    >
      <Icon size={13} />
      <span className="pointer-events-none absolute -top-7 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-md border border-app bg-surface px-1.5 py-0.5 text-[10px] text-app opacity-0 shadow-lg transition group-hover/btn:opacity-100">
        {label}
      </span>
    </button>
  );
}

function WorkspaceCard({ ws, index }: { ws: CodeWorkspace; index: number }) {
  const selectWorkspace = useHive((s) => s.selectWorkspace);
  const selectedId = useHive((s) => s.selectedWorkspaceId);
  const activeId = useHive((s) => s.activeWorkspaceId);
  const setActive = useHive((s) => s.setActiveWorkspace);
  const action = useHive((s) => s.workspaceAction);
  const aresEnabled = useHive((s) => isEnabled(s.entitlements, "ares"));
  const garlicVisible = useHive((s) => isVisible(s.entitlements, "garlic"));
  const byoncVisible = useHive((s) => isVisible(s.entitlements, "byonc"));
  const openProductPreview = useHive((s) => s.openProductPreview);
  const byoncPolicies = useHive((s) => s.byoncPolicies);
  const Provider = PROVIDER_ICON[ws.provider];
  const Product = APPS[ws.product].icon;
  const selected = selectedId === ws.id;
  const isActive = activeId === ws.id;
  const git = isGitSource(ws.provider);

  const run = (a: Parameters<typeof action>[1]) => (e: React.MouseEvent) => {
    e.stopPropagation();
    action(ws.id, a);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
      onClick={() => selectWorkspace(ws.id)}
      whileHover={{ y: -2 }}
      className="cursor-pointer rounded-2xl border bg-surface/50 p-4 transition"
      style={{
        borderColor: selected ? accent : "hsl(var(--border))",
        boxShadow: selected ? `0 10px 30px -14px ${accent}88` : undefined,
      }}
    >
      <div className="flex items-start gap-3">
        <span
          className="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-white shadow"
          style={{ background: `linear-gradient(135deg, ${ws.accent}, ${ws.accent}aa)` }}
        >
          <GitBranch size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-app">{ws.name}</span>
            {ws.isSample && <SampleBadge />}
            {isActive && (
              <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-emerald-500">
                active
              </span>
            )}
          </div>
          <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-app-2">
            <Provider size={11} className="shrink-0" />
            <span className="min-w-0 truncate" title={ws.repo}>{ws.repo}</span>
            {git && (
              <>
                <span className="shrink-0 text-app-2/50">·</span>
                <span className="max-w-[40%] flex-none truncate font-mono" title={ws.branch}>{ws.branch}</span>
              </>
            )}
          </div>
        </div>
        {ws.archived ? (
          <Badge tone="paused">
            <Dot tone="paused" /> Archived
          </Badge>
        ) : (
          <Badge tone={WS_STATUS_TONE[ws.status]}>
            <Dot tone={WS_STATUS_TONE[ws.status]} /> {WS_STATUS_LABEL[ws.status]}
          </Badge>
        )}
      </div>

      {/* meta chips */}
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <span className="flex items-center gap-1 rounded-md bg-surface-2/70 px-2 py-1 text-[11px] text-app-1">
          <Product size={11} style={{ color: APPS[ws.product].accent }} /> {APPS[ws.product].name}
        </span>
        <span className="rounded-md bg-surface-2/70 px-2 py-1 text-[11px] text-app-1">{WS_ENV_LABEL[ws.env]}</span>
        {git && <span className="rounded-md bg-surface-2/70 px-2 py-1 text-[11px] text-app-1">{ws.baseBranch} base</span>}
        {ws.garlic !== "none" && garlicVisible && (
          <span className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px]" style={{ background: "#a855f71f", color: "#a855f7" }}>
            <Sparkles size={10} /> {ws.garlic === "active" ? "Specialized" : "Garlic ready"}
          </span>
        )}
        {byoncVisible && (
          <span className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px]" style={{ background: "#10b9811f", color: "#10b981" }}>
            <Network size={10} /> {byoncPolicyLabel(ws.byonc, byoncPolicies)}
          </span>
        )}
      </div>

      {/* stats */}
      <div className="mt-3 grid grid-cols-4 gap-2 border-t border-app pt-3 text-center">
        <div>
          <div className="flex items-center justify-center gap-1 text-sm font-semibold text-app tabular-nums">
            <Cpu size={12} className="text-app-2" /> {ws.activeAgents}
          </div>
          <div className="text-[10px] text-app-2">agents</div>
        </div>
        <div>
          <div className="flex items-center justify-center gap-1 text-sm font-semibold text-app tabular-nums">
            <ListTodo size={12} className="text-app-2" /> {ws.openTasks}
          </div>
          <div className="text-[10px] text-app-2">tasks</div>
        </div>
        <div>
          <div className="flex items-center justify-center gap-1 text-sm font-semibold text-app tabular-nums">
            <HardDrive size={12} className="text-app-2" /> {ws.usage.disk}
          </div>
          <div className="text-[10px] text-app-2">disk</div>
        </div>
        <div>
          <div className="flex items-center justify-center gap-1 text-sm font-semibold text-app tabular-nums">
            <Clock size={12} className="text-app-2" /> {ws.usage.runtime}
          </div>
          <div className="text-[10px] text-app-2">runtime</div>
        </div>
      </div>

      {/* actions */}
      {ws.archived ? (
        <div className="mt-3 flex items-center justify-end">
          <button
            onClick={run("unarchive")}
            className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-black transition hover:brightness-105"
            style={{ background: accent }}
          >
            <ArchiveRestore size={12} /> Unarchive
          </button>
        </div>
      ) : (
      <div className="mt-3 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {isActive ? (
            aresEnabled ? (
              <button
                onClick={run("verify")}
                className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-black transition hover:brightness-105"
                style={{ background: accent }}
              >
                <ShieldCheck size={12} /> Verify
              </button>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openProductPreview("ares");
                }}
                title="Verification is powered by ARES (Early Access)"
                className="flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold transition hover:brightness-110"
                style={{ borderColor: APPS.ares.accent, color: APPS.ares.accent, background: `${APPS.ares.accent}14` }}
              >
                <ShieldCheck size={12} /> Verify · Early Access
              </button>
            )
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setActive(ws.id);
              }}
              className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-black transition hover:brightness-105"
              style={{ background: accent }}
            >
              <Play size={12} /> Open
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          {aresEnabled && !ws.isSample && aresWorkspaceUrl(ws.id) && (
            <IconButton
              icon={ExternalLink}
              label="Open in ARES"
              onClick={(e) => {
                e.stopPropagation();
                launchInAres(ws.id);
              }}
              primary
            />
          )}
          {ws.status === "sleeping" ? (
            <IconButton icon={Play} label="Resume" onClick={run("resume")} primary />
          ) : (
            <IconButton icon={Moon} label="Sleep" onClick={run("sleep")} />
          )}
          <IconButton icon={RefreshCw} label="Sync" onClick={run("sync")} />
          <IconButton icon={Copy} label="Clone" onClick={run("clone")} />
          <IconButton icon={Archive} label="Archive" onClick={run("archive")} danger />
        </div>
      </div>
      )}
    </motion.div>
  );
}

function DetailSection({ title, children, right }: { title: string; children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div>
      <SectionTitle right={right}>{title}</SectionTitle>
      {children}
    </div>
  );
}

function WorkspaceDetail({ ws }: { ws: CodeWorkspace }) {
  const tasks = useHive((s) => s.tasks).filter((t) => t.workspaceId === ws.id);
  const integrations = useHive((s) => s.integrations);
  const openApp = useHive((s) => s.openApp);
  const restoreSnapshot = useHive((s) => s.restoreSnapshot);
  const action = useHive((s) => s.workspaceAction);
  const aresEnabled = useHive((s) => isEnabled(s.entitlements, "ares"));
  const garlicVisible = useHive((s) => isVisible(s.entitlements, "garlic"));
  const byoncVisible = useHive((s) => isVisible(s.entitlements, "byonc"));
  const openProductPreview = useHive((s) => s.openProductPreview);
  const byoncPolicies = useHive((s) => s.byoncPolicies);
  const Product = APPS[ws.product].icon;
  const linked = integrations.filter((i) => ws.integrations.includes(i.id));

  return (
    <motion.div
      key={ws.id}
      initial={{ opacity: 0, x: 14 }}
      animate={{ opacity: 1, x: 0 }}
      className="flex h-full flex-col"
    >
      {/* header */}
      <div className="border-b border-app p-4">
        <div className="flex items-start gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-xl text-white shadow" style={{ background: `linear-gradient(135deg, ${ws.accent}, ${ws.accent}aa)` }}>
            <GitBranch size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-base font-semibold text-app">{ws.name}</div>
            <div className="truncate font-mono text-xs text-app-2">{ws.repo} · {ws.branch}</div>
          </div>
          <Badge tone={WS_STATUS_TONE[ws.status]}>
            <Dot tone={WS_STATUS_TONE[ws.status]} /> {WS_STATUS_LABEL[ws.status]}
          </Badge>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {aresEnabled ? (
            <button
              onClick={() => action(ws.id, "verify")}
              className="flex items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-semibold text-black transition hover:brightness-105"
              style={{ background: accent }}
            >
              <ShieldCheck size={13} /> Verify workspace
            </button>
          ) : (
            <button
              onClick={() => openProductPreview("ares")}
              title="Verification is powered by ARES (Early Access)"
              className="flex items-center justify-center gap-1.5 rounded-lg border py-2 text-xs font-semibold transition hover:brightness-110"
              style={{ borderColor: APPS.ares.accent, color: APPS.ares.accent, background: `${APPS.ares.accent}14` }}
            >
              <ShieldCheck size={13} /> Verify · Requires ARES
            </button>
          )}
          <button
            onClick={() => action(ws.id, "sync")}
            className="flex items-center justify-center gap-1.5 rounded-lg border border-app bg-surface-2/60 py-2 text-xs font-medium text-app-1 transition hover:border-app-strong"
          >
            <RefreshCw size={13} /> Sync branch
          </button>
          {aresEnabled && !ws.isSample && aresWorkspaceUrl(ws.id) && (
            <button
              onClick={() => launchInAres(ws.id)}
              className="col-span-2 flex items-center justify-center gap-1.5 rounded-lg border py-2 text-xs font-semibold transition hover:brightness-110"
              style={{ borderColor: APPS.ares.accent, background: `${APPS.ares.accent}1f`, color: APPS.ares.accent }}
            >
              <ExternalLink size={13} /> Open in ARES
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 space-y-5 overflow-y-auto no-scrollbar p-4">
        {/* AI suggestions */}
        {ws.suggestions.length > 0 && (
          <DetailSection title="Smart suggestions">
            <div className="space-y-1.5">
              {ws.suggestions.map((s) => (
                <div key={s.id} className="flex items-start gap-2 rounded-lg border border-app bg-surface-2/40 p-2.5">
                  <span className="mt-0.5">
                    {s.tone === "warning" ? (
                      <AlertTriangle size={13} className="text-amber-500" />
                    ) : s.tone === "success" ? (
                      <Sparkles size={13} className="text-emerald-500" />
                    ) : (
                      <Lightbulb size={13} className="text-sky-500" />
                    )}
                  </span>
                  <span className="flex-1 text-xs leading-relaxed text-app-1">{s.text}</span>
                  <button
                    onClick={() => {
                      if (/garlic/i.test(s.action)) openApp("garlic");
                      else if (/byonc/i.test(s.action)) openApp("byonc");
                      else if (/sync/i.test(s.action)) action(ws.id, "sync");
                      else if (/task/i.test(s.action)) openApp("tasks");
                    }}
                    className="shrink-0 rounded-md px-2 py-1 text-[10px] font-medium transition"
                    style={{ background: `${accent}1f`, color: accent }}
                  >
                    {s.action}
                  </button>
                </div>
              ))}
            </div>
          </DetailSection>
        )}

        {/* Repo + branch overview */}
        <DetailSection title="Repo overview">
          <div className="grid grid-cols-2 gap-2">
            {[
              ["Source", WS_PROVIDER_LABEL[ws.provider]],
              ["Language", ws.lang],
              ...(isGitSource(ws.provider)
                ? [
                    ["Base branch", ws.baseBranch],
                    ["Behind base", `${ws.commitsBehind} commits`],
                  ]
                : []),
              ...(ws.localPath ? [["Local path", ws.localPath]] : []),
              ...(ws.s3Bucket
                ? [["S3 location", ws.s3Prefix ? `${ws.s3Bucket}/${ws.s3Prefix}` : ws.s3Bucket]]
                : []),
              ["Linked product", APPS[ws.product].name],
              ["Environment", WS_ENV_LABEL[ws.env]],
            ].map(([k, v]) => (
              <div key={k as string} className="rounded-lg border border-app bg-surface-2/30 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-app-2">{k}</div>
                <div className="mt-0.5 truncate text-sm font-medium text-app" title={String(v)}>{v}</div>
              </div>
            ))}
          </div>
        </DetailSection>

        {/* Recent commits */}
        <DetailSection title="Recent commits" right={<span className="text-[11px] text-app-2">{ws.commits.length}</span>}>
          {ws.commits.length === 0 ? (
            <div className="rounded-lg border border-dashed border-app p-3 text-center text-xs text-app-2">No commits yet</div>
          ) : (
            <div className="space-y-1">
              {ws.commits.map((c) => (
                <div key={c.hash} className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-surface-2/50">
                  <GitCommitHorizontal size={14} className="shrink-0 text-app-2" />
                  <span className="min-w-0 flex-1 truncate text-xs text-app">{c.msg}</span>
                  <span className="font-mono text-[10px] text-app-2">{c.hash.slice(0, 7)}</span>
                  <span className="text-[10px] text-app-2">{c.time}</span>
                </div>
              ))}
            </div>
          )}
        </DetailSection>

        {/* Linked tasks (cross-widget) */}
        <DetailSection
          title="Linked tasks"
          right={
            <button onClick={() => openApp("tasks")} className="flex items-center gap-1 text-[11px] text-app-2 hover:text-app">
              Open Tasks <ArrowRight size={11} />
            </button>
          }
        >
          {tasks.length === 0 ? (
            <div className="rounded-lg border border-dashed border-app p-3 text-center text-xs text-app-2">No linked tasks</div>
          ) : (
            <div className="space-y-1.5">
              {tasks.slice(0, 4).map((t) => (
                <button
                  key={t.id}
                  onClick={() => {
                    useHive.getState().selectTask(t.id);
                    openApp("tasks");
                  }}
                  className="flex w-full items-center gap-2 rounded-lg border border-app bg-surface-2/30 p-2 text-left transition hover:border-app-strong"
                >
                  <span className="font-mono text-[10px] text-app-2">{t.id}</span>
                  <span className="min-w-0 flex-1 truncate text-xs text-app">{t.title}</span>
                  <Badge tone={t.status === "ready" ? "connected" : t.status === "blocked" ? "error" : "queued"}>{t.status.replace(/-/g, " ")}</Badge>
                </button>
              ))}
            </div>
          )}
        </DetailSection>

        {/* Active agents */}
        <DetailSection title="Active agents">
          {ws.agents.length === 0 ? (
            <div className="rounded-lg border border-dashed border-app p-3 text-center text-xs text-app-2">No agents running</div>
          ) : (
            <div className="space-y-1.5">
              {ws.agents.map((a) => (
                <div key={a.name} className="flex items-center gap-2.5 rounded-lg border border-app bg-surface-2/30 p-2.5">
                  <span className="grid h-7 w-7 place-items-center rounded-lg bg-surface-2"><Cpu size={13} style={{ color: accent }} /></span>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-app">{a.name}</div>
                    <div className="truncate text-[11px] text-app-2">{a.task}</div>
                  </div>
                  <Dot tone="running" />
                </div>
              ))}
            </div>
          )}
        </DetailSection>

        {/* Verification history */}
        <DetailSection title="Verification history">
          <div className="space-y-1">
            {ws.verifications.map((v, i) => (
              <div key={i} className="flex items-center gap-2 rounded-lg px-2 py-1.5">
                {v.result === "pass" ? (
                  <CheckCircle2 size={14} className="text-emerald-500" />
                ) : (
                  <XCircle size={14} className="text-rose-500" />
                )}
                <span className="flex-1 text-xs text-app">{v.label}</span>
                <span className="text-[10px] text-app-2">{v.time}</span>
              </div>
            ))}
          </div>
        </DetailSection>

        {/* Environment health + secrets */}
        <div className="grid grid-cols-2 gap-3">
          <DetailSection title="Environment health">
            <div className="space-y-2 rounded-lg border border-app bg-surface-2/30 p-3">
              {Object.entries(ws.health).map(([k, v]) => {
                const ok = /pass|green|up to date/i.test(v);
                const bad = /fail|block/i.test(v);
                return (
                  <div key={k} className="flex items-center justify-between text-xs">
                    <span className="capitalize text-app-2">{k}</span>
                    <span className={ok ? "text-emerald-500" : bad ? "text-rose-500" : "text-amber-500"}>{v}</span>
                  </div>
                );
              })}
              <div className="border-t border-app pt-2">
                <div className="mb-1 flex justify-between text-[10px] text-app-2"><span>CPU</span><span>{Math.round(ws.usage.cpu * 100)}%</span></div>
                <Progress value={ws.usage.cpu} color={accent} />
                <div className="mb-1 mt-2 flex justify-between text-[10px] text-app-2"><span>Memory</span><span>{Math.round(ws.usage.mem * 100)}%</span></div>
                <Progress value={ws.usage.mem} color="#a855f7" />
              </div>
            </div>
          </DetailSection>
          <DetailSection title="Secrets & routing">
            <div className="space-y-2 rounded-lg border border-app bg-surface-2/30 p-3 text-xs">
              <div className="flex items-center gap-2">
                <KeyRound size={13} className="text-app-2" />
                <span className="font-mono text-app">{ws.secretsProfile}</span>
              </div>
              <div className="flex items-center gap-2">
                <Server size={13} className="text-app-2" />
                <span className="text-app">{ws.compute}</span>
              </div>
              {byoncVisible && (
                <button
                  onClick={() => openApp("byonc")}
                  className="flex w-full items-center gap-2 rounded-lg border border-app px-2 py-1.5 transition hover:border-app-strong"
                >
                  <Network size={13} className="text-emerald-500" />
                  <span className="flex-1 text-left text-app">{byoncPolicyLabel(ws.byonc, byoncPolicies)}</span>
                  <ArrowRight size={11} className="text-app-2" />
                </button>
              )}
              {ws.garlic !== "none" && garlicVisible && (
                <button
                  onClick={() => openApp("garlic")}
                  className="flex w-full items-center gap-2 rounded-lg border border-app px-2 py-1.5 transition hover:border-app-strong"
                >
                  <Sparkles size={13} className="text-violet-500" />
                  <span className="flex-1 text-left text-app">{ws.garlic === "active" ? "Specialization active" : "Specialization available"}</span>
                  <ArrowRight size={11} className="text-app-2" />
                </button>
              )}
            </div>
          </DetailSection>
        </div>

        {/* Integrations (cross-widget) */}
        <DetailSection
          title="Integrations"
          right={
            <button onClick={() => openApp("integrations")} className="flex items-center gap-1 text-[11px] text-app-2 hover:text-app">
              Integration Center <ArrowRight size={11} />
            </button>
          }
        >
          <div className="flex flex-wrap gap-1.5">
            {linked.map((i) => (
              <span
                key={i.id}
                className="flex items-center gap-1.5 rounded-lg border border-app bg-surface-2/40 px-2 py-1 text-[11px] text-app-1"
              >
                <span className="grid h-4 w-4 place-items-center rounded text-[9px] font-bold" style={{ background: `${i.color}22`, color: i.color }}>
                  {i.glyph}
                </span>
                {i.name}
                {i.status !== "connected" && <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />}
              </span>
            ))}
          </div>
        </DetailSection>

        {/* Snapshots */}
        <DetailSection title="Snapshots" right={<span className="text-[11px] text-app-2">{ws.snapshots.length}</span>}>
          {ws.snapshots.length === 0 ? (
            <div className="rounded-lg border border-dashed border-app p-3 text-center text-xs text-app-2">No snapshots saved</div>
          ) : (
            <div className="space-y-1.5">
              {ws.snapshots.map((sn) => (
                <div key={sn.id} className="flex items-center gap-2.5 rounded-lg border border-app bg-surface-2/30 p-2.5">
                  <Camera size={14} className="shrink-0 text-app-2" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-medium text-app">{sn.name}</div>
                    <div className="text-[10px] text-app-2">{sn.createdAt} · {sn.size}{sn.verified ? " · verified" : ""}</div>
                  </div>
                  <button onClick={() => restoreSnapshot(ws.id, sn.id)} title="Restore" className="grid h-6 w-6 place-items-center rounded-md border border-app text-app-2 transition hover:border-app-strong hover:text-app">
                    <RefreshCw size={12} />
                  </button>
                  <button onClick={() => useHive.getState().pushToast({ title: "Forked from snapshot", body: sn.name, tone: "success" })} title="Fork" className="grid h-6 w-6 place-items-center rounded-md border border-app text-app-2 transition hover:border-app-strong hover:text-app">
                    <GitFork size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <button
            onClick={() => useHive.getState().pushToast({ title: "Snapshot saved", body: `${ws.repo} · ${ws.branch}`, tone: "success" })}
            className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-app py-2 text-xs text-app-2 transition hover:border-app-strong hover:text-app"
          >
            <Camera size={13} /> Save snapshot
          </button>
        </DetailSection>

        {/* Activity timeline */}
        <DetailSection title="Activity timeline">
          <div className="relative space-y-1 pl-4">
            <div className="absolute bottom-2 left-[5px] top-2 w-px bg-app" />
            {ws.timeline.map((e, i) => (
              <div key={i} className="relative py-1">
                <span className="absolute -left-[14px] top-2 h-2.5 w-2.5 rounded-full border-2 border-surface" style={{ background: accent }} />
                <div className="flex items-center justify-between">
                  <div className="text-xs font-medium text-app">{e.label}</div>
                  <span className="text-[10px] text-app-2">{e.time}</span>
                </div>
                <div className="text-[11px] text-app-2">{e.detail}</div>
              </div>
            ))}
          </div>
        </DetailSection>
      </div>
    </motion.div>
  );
}

function EmptyState() {
  const setWizard = useHive((s) => s.setWsWizard);
  const openApp = useHive((s) => s.openApp);
  const aresEnabled = useHive((s) => isEnabled(s.entitlements, "ares"));
  const sampleCta = useSampleDataCta();
  return (
    <div className="grid h-full place-items-center p-10 text-center">
      <div>
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="mx-auto grid h-20 w-20 place-items-center rounded-3xl text-white shadow-glow"
          style={{ background: `linear-gradient(135deg, ${accent}, ${accent}aa)` }}
        >
          <FolderGit2 size={34} />
        </motion.div>
        <h2 className="mt-5 text-xl font-semibold text-app">Create your first cloud workspace</h2>
        <p className="mx-auto mt-2 max-w-sm text-sm text-app-2">
          {aresEnabled
            ? "A workspace pairs a repository, branch and environment with ARES agents and everything Hive knows about your project."
            : "A workspace pairs a repository, branch and environment with everything Hive knows about your project."}
        </p>
        <div className="mt-6 flex items-center justify-center gap-2">
          <button onClick={() => setWizard(true)} className="flex items-center gap-1.5 rounded-lg px-4 py-2.5 text-sm font-semibold text-black transition hover:brightness-105" style={{ background: accent }}>
            <Github size={15} /> Connect GitHub
          </button>
          <button onClick={() => setWizard(true)} className="flex items-center gap-1.5 rounded-lg border border-app px-4 py-2.5 text-sm font-medium text-app-1 transition hover:border-app-strong">
            <Box size={15} /> Import Repository
          </button>
          <button onClick={() => openApp("tasks")} className="flex items-center gap-1.5 rounded-lg border border-app px-4 py-2.5 text-sm font-medium text-app-1 transition hover:border-app-strong">
            Start from Jira Ticket
          </button>
        </div>
        {sampleCta && (
          <button
            onClick={sampleCta.run}
            className="mx-auto mt-3 flex items-center gap-1.5 rounded-lg border border-dashed border-app px-4 py-2 text-sm font-medium text-app-1 transition hover:border-app-strong"
          >
            <Sparkles size={14} /> {sampleCta.label}
          </button>
        )}
      </div>
    </div>
  );
}

export function WorkspaceManagerApp() {
  const workspaces = useHive((s) => s.codeWorkspaces);
  const selectedId = useHive((s) => s.selectedWorkspaceId);
  const setWizard = useHive((s) => s.setWsWizard);
  const [filter, setFilter] = useState<"all" | "active" | "attention" | "archived">("all");

  const selected = workspaces.find((w) => w.id === selectedId);
  // Archived workspaces live only under the "archived" tab.
  const live = workspaces.filter((w) => !w.archived);
  const archivedCount = workspaces.length - live.length;
  const attentionCount = live.filter((w) => ["error", "conflict", "needs-sync"].includes(w.status)).length;
  const activeCount = live.filter((w) => w.status === "active" || w.status === "verifying").length;

  const filtered =
    filter === "archived"
      ? workspaces.filter((w) => w.archived)
      : live.filter((w) =>
          filter === "all"
            ? true
            : filter === "active"
              ? w.status === "active" || w.status === "verifying"
              : ["error", "conflict", "needs-sync"].includes(w.status)
        );

  if (workspaces.length === 0) {
    return (
      <div className="relative min-h-full">
        <EmptyState />
        <WorkspaceWizard />
      </div>
    );
  }

  return (
    <div className="relative flex h-full flex-col">
      <AppHeader
        icon={FolderGit2}
        title="Workspace Manager"
        subtitle={`${live.length} workspaces · ${activeCount} active · ${attentionCount} need attention`}
        accent={accent}
        actions={
          <button
            onClick={() => setWizard(true)}
            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-black transition hover:brightness-105"
            style={{ background: accent }}
          >
            <Plus size={13} /> New Workspace
          </button>
        }
      />

      <div className="flex min-h-0 flex-1">
        {/* List column */}
        <div className="flex min-w-0 flex-1 flex-col border-r border-app">
          <div className="flex items-center gap-1.5 border-b border-app px-4 py-2.5">
            {(["all", "active", "attention", "archived"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className="rounded-lg px-2.5 py-1 text-xs font-medium capitalize transition"
                style={{
                  background: filter === f ? `${accent}1f` : "transparent",
                  color: filter === f ? accent : "var(--text-1)",
                }}
              >
                {f}
                {f === "attention" && attentionCount > 0 && <span className="ml-1 text-amber-500">{attentionCount}</span>}
                {f === "archived" && archivedCount > 0 && <span className="ml-1 text-app-2">{archivedCount}</span>}
              </button>
            ))}
            <span className="ml-auto text-[11px] text-app-2">{filtered.length} shown</span>
          </div>
          <div className="grid flex-1 grid-cols-1 content-start gap-3 overflow-y-auto no-scrollbar p-4 xl:grid-cols-2">
            <AnimatePresence>
              {filtered.map((ws, i) => (
                <WorkspaceCard key={ws.id} ws={ws} index={i} />
              ))}
            </AnimatePresence>
          </div>
        </div>

        {/* Detail column */}
        <div className="hidden w-[420px] shrink-0 lg:block">
          <AnimatePresence mode="wait">
            {selected ? (
              <WorkspaceDetail ws={selected} />
            ) : (
              <motion.div
                key="empty-detail"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="grid h-full place-items-center p-8 text-center"
              >
                <div>
                  <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-surface-2 text-app-2">
                    <Plug size={22} />
                  </div>
                  <p className="mt-3 text-sm text-app-2">Select a workspace to inspect its repo, agents, verification and timeline.</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <WorkspaceWizard />
    </div>
  );
}
